/**
 * SidebarContext — shared state for the desktop sidebar collapsed flag.
 *
 * Both Sidebar (the rail itself) and Header (which mirrors the rail
 * width with the OVACS logo) need to read and react to this state.
 * Lifted here so the logo can swap between full mark and icon-only
 * without prop drilling.
 *
 * Persisted to localStorage under `ovacs.sidebarCollapsed` ('1' | '0').
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const STORAGE_KEY = 'ovacs.sidebarCollapsed'

function readStored() {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const SidebarContext = createContext({
  collapsed: false,
  setCollapsed: () => {},
  toggleCollapsed: () => {},
})

export function SidebarProvider({ children }) {
  const [collapsed, setCollapsedState] = useState(readStored)

  const setCollapsed = useCallback((next) => {
    setCollapsedState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
    } catch { /* quota — ignore */ }
  }, [])

  const toggleCollapsed = useCallback(() => {
    setCollapsedState(prev => {
      const next = !prev
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch { /* quota — ignore */ }
      return next
    })
  }, [])

  // Sync across tabs in case the user has the app open in two places
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setCollapsedState(e.newValue === '1')
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, toggleCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  return useContext(SidebarContext)
}
